/**
 * PricingRuleTypePickerWidget — Visual card picker for Pricing Rule type.
 *
 * Shows 3 pricing rule types as tappable icon cards:
 *   1. ลดราคา (Price Discount) — set a rate, % off, or fixed discount
 *   2. คูปอง (Coupon Code) — same as price discount but coupon_code_based=1
 *   3. แถมสินค้า (Free Item) — give free items as product discount
 *
 * Auto-sets derived fields based on selection:
 *   - coupon_code_based, price_or_product_discount, apply_on, selling
 *
 * Follows StockEntryTypePickerWidget pattern.
 */

import { cn } from "@/lib/utils";
import { Check, Percent, Ticket, Gift } from "lucide-react";
import type { ExperienceWidgetProps } from "@/lib/views/widgets/registry";
import { SectionHeader } from "@/features/inventory/item-detail/components/SectionHeader";
import { useLanguage } from "@/i18n/useLanguage";
import { getSectionTitleTranslation } from "@/lib/views/labels/translations";
import { CARD } from "@/config/design-tokens";

interface RuleTypeConfig {
  id: string;
  icon: typeof Percent;
  label: { en: string; th: string };
  desc: { en: string; th: string };
  /** Fields auto-set when this type is selected */
  autoSet: Record<string, unknown>;
}

const RULE_TYPES: RuleTypeConfig[] = [
  {
    id: "price_discount",
    icon: Percent,
    label: { en: "Price Discount", th: "ลดราคา" },
    desc: { en: "Set rate, % off, or fixed discount", th: "กำหนดราคา ส่วนลด % หรือส่วนลดคงที่" },
    autoSet: {
      price_or_product_discount: "Price",
      coupon_code_based: 0,
      selling: 1,
      buying: 0,
    },
  },
  {
    id: "coupon",
    icon: Ticket,
    label: { en: "Coupon Code", th: "คูปอง" },
    desc: { en: "Customer enters code at checkout", th: "ลูกค้าใส่โค้ดตอนชำระเงิน" },
    autoSet: {
      price_or_product_discount: "Price",
      coupon_code_based: 1,
      apply_on: "Transaction",
      selling: 1,
      buying: 0,
    },
  },
  {
    id: "product_discount",
    icon: Gift,
    label: { en: "Free Item", th: "แถมสินค้า" },
    desc: { en: "Give free items when conditions are met", th: "แถมสินค้าเมื่อตรงเงื่อนไข" },
    autoSet: {
      price_or_product_discount: "Product",
      coupon_code_based: 0,
      selling: 1,
      buying: 0,
    },
  },
];

/** Derive the selected type ID from formData */
function getSelectedType(formData: Record<string, unknown>): string {
  const discountType = formData.price_or_product_discount;
  const isCoupon = formData.coupon_code_based;

  if (discountType === "Product") return "product_discount";
  if (isCoupon) return "coupon";
  if (discountType === "Price") return "price_discount";
  return "";
}

export function PricingRuleTypePickerWidget({
  formData,
  onFieldChange,
  disabled,
  isSaving,
}: ExperienceWidgetProps) {
  const { language } = useLanguage();
  const selectedType = getSelectedType(formData);

  const handleSelect = (ruleType: RuleTypeConfig) => {
    if (disabled || isSaving) return;

    // Apply all auto-set fields
    for (const [field, value] of Object.entries(ruleType.autoSet)) {
      onFieldChange(field, value);
    }
  };

  return (
    <div>
      <SectionHeader
        label={getSectionTitleTranslation("Pricing Rule Type", language)}
        subtitle={language === "th" ? "เลือกประเภทส่วนลด" : "What kind of discount?"}
      />
      <div className={cn(CARD, "p-5")}>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {RULE_TYPES.map((ruleType) => {
            const isSelected = selectedType === ruleType.id;
            const Icon = ruleType.icon;
            const label = language === "th" ? ruleType.label.th : ruleType.label.en;
            const desc = language === "th" ? ruleType.desc.th : ruleType.desc.en;

            return (
              <button
                key={ruleType.id}
                type="button"
                onClick={() => handleSelect(ruleType)}
                disabled={disabled || isSaving}
                className={cn(
                  "relative flex flex-col items-center gap-2.5 rounded-xl p-5 text-center",
                  "border-2 transition-all duration-200",
                  "hover:bg-accent/50 active:scale-[0.98]",
                  "disabled:opacity-50 disabled:cursor-not-allowed",
                  isSelected
                    ? "border-primary bg-primary/5 shadow-sm"
                    : "border-border/40 bg-background"
                )}
              >
                {isSelected && (
                  <div className="absolute top-2 right-2">
                    <Check className="h-4 w-4 text-primary" />
                  </div>
                )}
                <div
                  className={cn(
                    "flex items-center justify-center h-11 w-11 rounded-xl",
                    isSelected
                      ? "bg-primary text-white"
                      : "bg-muted text-muted-foreground"
                  )}
                >
                  <Icon className="h-5 w-5" />
                </div>
                <div>
                  <div className="text-sm font-semibold leading-tight">{label}</div>
                  <div className="text-[11px] text-muted-foreground mt-1 leading-tight">
                    {desc}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
